import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FileItem } from 'ng2-file-upload';
import { Observable } from 'rxjs/Observable';
import { environment } from '../../../environments/environment';
import { timeout } from 'rxjs/operators';
import { Area } from '../../core/constants/area';
import { FileResultResponse } from './models/responses/file-result.response';

@Injectable()
export class DocumentUploaderService {

  constructor(public httpClient: HttpClient) { }

  public handleExtension(
    fileName: string,
    extension: string,
    overrideExtension: boolean = false
  ): string {

    const maxLength = 250;

    const allowedExtensions: string[] = [
      '.pdf', '.doc', '.docx',
      '.xls', '.xlsx', '.csv',
      '.txt', '.rtf',
      '.jpg', '.jpeg', '.png',
      '.gif', '.bmp', '.webp',
      '.zip', '.msg', '.eml'
    ];

    // Remove wrapping quotes
    fileName = fileName.replace(/^["']+|["']+$/g, '');

    // Normalize requested extension
    let normalizedExtension = extension.startsWith('.')
      ? extension.toLowerCase()
      : '.' + extension.toLowerCase();

    // If requested extension is not allowed, default to .pdf
    if (allowedExtensions.indexOf(normalizedExtension) === -1) {
      normalizedExtension = '.pdf';
    }

    // Check if filename already ends with a trusted extension
    const extensionMatch = fileName.match(/\.([a-z0-9]{1,10})$/i);
    let hasTrustedExtension = false;

    if (extensionMatch) {
      const detectedExtension = '.' + extensionMatch[1].toLowerCase();
      hasTrustedExtension = allowedExtensions.indexOf(detectedExtension) !== -1;
    }

    // If overriding, always append correct extension
   if (overrideExtension) {

      let baseName = fileName;

      // If it already ends with a trusted extension, remove it first
      const matchedExtension = fileName.match(/\.([a-z0-9]{1,10})$/i);
      if (matchedExtension) {
        const detectedExtension = '.' + matchedExtension[1].toLowerCase();
        if (allowedExtensions.indexOf(detectedExtension) !== -1) {
          baseName = fileName.substring(0, fileName.length - detectedExtension.length);
        }
      }

      const shortenedBase = baseName.substring(0, maxLength - normalizedExtension.length);
      return shortenedBase + normalizedExtension;
    }

    // If already has trusted extension, leave it
    if (hasTrustedExtension) {
      if (fileName.length <= maxLength) {
        return fileName;
      }

      const ext = extensionMatch ? extensionMatch[0] : '';
      const base = fileName.substring(0, fileName.length - ext.length);
      const maxBaseLength = maxLength - ext.length;

      return base.substring(0, maxBaseLength) + ext;
    }

    // Otherwise append the trusted extension
    const trimmedBase = fileName.substring(0, maxLength - normalizedExtension.length);
    return trimmedBase + normalizedExtension;
  }

  uploadDocument(uploadedFile:FileItem, documentId, square9ArchiveID, square9DatabaseID, companyID, note) {
    
    const headers = new HttpHeaders();
    headers.set('Content-Type', null);
    headers.set('Accept', "multipart/form-data");
    const params = new HttpParams();

    const file = uploadedFile.file;
    const blob = new Blob([file.rawFile], { type: file.type });
    const formData = new FormData();
    formData.append(`${documentId}-${file.name}`, blob, `${documentId}-${file.name}`);
    formData.append('documentId',`${documentId}`);
    formData.append('square9ArchiveID',`${square9ArchiveID}`);
    formData.append('square9DatabaseID',`${square9DatabaseID}`);
    formData.append('companyID',`${companyID}`);
    formData.append('tableName',`${note.tableName}`);
    formData.append('recordId',`${note.recordId}`);
    formData.append('noteTypeId',`${note.noteTypeId}`);
    formData.append('noteText',`${note.noteText}`);
    formData.append('pinned',`${note.pinned}`);
    formData.append('priority',`${note.priority}`);

    const url = `${environment.dotNetApiUrl}/documents/ImportDocument`;
    return this.httpClient.post<any>(url, formData, {params, headers, reportProgress: true, observe: 'events'})
  }

  // Upload document preserving original file extension (CSV, Excel, etc. - not converted to PDF)
  uploadDocumentPreserveExtension(uploadedFile: FileItem, documentId: number, square9ArchiveID: number, square9DatabaseID: number, companyID: number, note: { tableName: string; recordId: number; noteTypeId: number; noteText: string; pinned: boolean; priority: number }) {
    const headers = new HttpHeaders();
    headers.set('Content-Type', null);
    headers.set('Accept', "multipart/form-data");
    const params = new HttpParams();

    const file = uploadedFile.file;
    const blob = new Blob([file.rawFile], { type: file.type });
    const formData = new FormData();
    formData.append(`${documentId}-${file.name}`, blob, `${documentId}-${file.name}`);
    formData.append('documentId', `${documentId}`);
    formData.append('square9ArchiveID', `${square9ArchiveID}`);
    formData.append('square9DatabaseID', `${square9DatabaseID}`);
    formData.append('companyID', `${companyID}`);
    formData.append('tableName', `${note.tableName}`);
    formData.append('recordId', `${note.recordId}`);
    formData.append('noteTypeId', `${note.noteTypeId}`);
    formData.append('noteText', `${note.noteText}`);
    formData.append('pinned', `${note.pinned}`);
    formData.append('priority', `${note.priority}`);

    const url = `${environment.dotNetApiUrl}/documents/ImportDocumentPreserveExtension`;
    return this.httpClient.post<any>(url, formData, { params, headers, reportProgress: true, observe: 'events' });
  }

  viewDocument(Square9DocumentID, Square9ArchiveID, Square9DatabaseID, documentId, isDisplayOnly: boolean = false): Observable<any> {
    const httpOptions = {};
    const url = `${environment.dotNetApiUrl}/documents/RetrieveDocument?Square9DocumentID=${Square9DocumentID}&Square9ArchiveID=${Square9ArchiveID}&Square9DatabaseID=${Square9DatabaseID}&returnType=File&documentId=${documentId}&isDisplayOnly=${isDisplayOnly}`;
    return this.httpClient.get(url, httpOptions).pipe(timeout(30000));
  }
  viewDocumentByDocumentId(documentId: number, isDisplayOnly: boolean = false): Observable<any> {
    const httpOptions = {};
    const url = `${environment.dotNetApiUrl}/documents/RetrieveDocument/ByDocumentId?returnType=File&documentId=${documentId}&isDisplayOnly=${isDisplayOnly}`;
    return this.httpClient.get(url, httpOptions).pipe(timeout(30000));
  }

  combinedChecklistDocuments(checklistId: number, unitId: number): Observable<FileResultResponse> {
    const url = `${environment.dotNetApiUrl}/documents/ConnectedToChecklist?checklistId=${checklistId}&unitId=${unitId}`;
    return this.httpClient.get<FileResultResponse>(url);
  }

  pingSquare9(): Observable<any> {
    const httpOptions = {};
    const url = `${environment.dotNetApiUrl}/Documents/PingLicense`;
    return this.httpClient.get(url, httpOptions).pipe(timeout(30000));
  }

  importFile(uploadedFile:FileItem, importFileType: string) {
    const headers = new HttpHeaders();
    headers.set('Content-Type', null);
    headers.set('Accept', "multipart/form-data");
    const params = new HttpParams();

    const file = uploadedFile.file;
    const blob = new Blob([file.rawFile], { type: file.type });
    const formData = new FormData();
    formData.append(`${file.name}`, blob, `${file.name}`);
    formData.append('importFileType',`${importFileType}`);
    const url = `${environment.dotNetApiUrl}/${Area.core}/FileImport/ImportExternalFile`;
    return this.httpClient.post<any>(url, formData, {params, headers, reportProgress: true, observe: 'events'})
  }

  getImportedExternalDataList(fileType: string): Observable<any> {
    const httpOptions = {};
    const url = `${environment.dotNetApiUrl}/${Area.core}/FileImport/ImportedExternalDataList?importFileType=${fileType}`;
    return this.httpClient.get(url, httpOptions).pipe(timeout(30000));
  }

  getDocumentTypeServiceByDocumentType(documentTypeId): Observable<any> {
    const url = `${environment.dotNetApiUrl}/${Area.core}/documentType/Service/${documentTypeId}`;
    return this.httpClient.get<any[]>(url);
  }

  getDocumentList(companyId, isActive, isExpired, subServiceId, subServiceTypeId, documentTypeId){
    const url = `${environment.dotNetApiUrl}/${Area.core}/Document/List?CompanyId=${companyId}&IsActive=${isActive}&isExpired=${isExpired}&SubServiceId=${subServiceId}&SubServiceTypeId=${subServiceTypeId}&DocumentTypeId=${documentTypeId}`;
    return this.httpClient.get<any[]>(url);
  }

  getDocumentRoleList( documentTypeId){
    const url = `${environment.dotNetApiUrl}/${Area.core}/DocumentType/Role/List/${documentTypeId}`;
    return this.httpClient.get<any[]>(url);
  }

  postDocumentTypeById(document){
    const url = `${environment.dotNetApiUrl}/${Area.core}/Document`;
    return this.httpClient.post<any>(url, document);
  }

  replaceTaskDocument(document) {
    const url = `${environment.dotNetApiUrl}/${Area.core}/Document/ReplaceTaskDocument`;
    return this.httpClient.post<any>(url, document);
  }

  getDocumentListByTaskId(taskId){
    const url = `${environment.dotNetApiUrl}/${Area.core}/Document/ByTaskId?taskId=${taskId}`;
    return this.httpClient.get<any[]>(url);
  }

  getDocumentListByTaskIdAndDocumentType(taskId, documentTypeId){
    const url = `${environment.dotNetApiUrl}/${Area.core}/Document/ByTaskIdAndDocumentType?taskId=${taskId}&documentTypeId=${documentTypeId}`;
    return this.httpClient.get<any[]>(url);
  }

  patchProcessQueue(queue: string){
    const url = `${environment.dotNetApiUrl}/${Area.core}/FileImport/ProcessQueue?queue=${queue}`;
    return this.httpClient.patch<any[]>(url, {});
  }

  getBlobStorageByDocumentIds(documentIds: number[]) {
    let url = `${environment.dotNetApiUrl}/${Area.core}/BlobStorage/downloadByDocumentIds?`;
    documentIds.forEach(documentId =>{
      url += `&documentIds=${documentId}`
    })
    return this.httpClient.get<any[]>(url);
  }

  getDocumentTypesByChecklistInfo(checklistId: number, checklistUrl: string, name: string, secondName: string){
    const url = `${environment.dotNetApiUrl}/${Area.core}/documentType/ChecklistUrl?checklistId=${checklistId}&url=${checklistUrl}&name=${name}&secondName=${secondName}`;
    return this.httpClient.get<any[]>(url);
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/documents/document-uploader.service.ts